import React from "react";
import { useTranslation, LANGUAGE_CONFIGS } from "@/locales";
import type { Language } from "@/types/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDownIcon, Globe } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "./ui/button";

interface LanguageSwitcherProps {
  className?: string;
  showIcon?: boolean;
  variant?: "default" | "compact";
}

export default function LanguageSwitcher({
  className = "",
  showIcon = true,
  variant = "default",
}: LanguageSwitcherProps) {
  const { language, setLanguage } = useTranslation();
  const isMobile = useIsMobile();

  const handleLanguageChange = (value: string) => {
    setLanguage(value as Language);
  };

  const currentConfig = LANGUAGE_CONFIGS.find(
    (config) => config.code === language
  );

  if (variant === "compact") {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {showIcon && <Globe className="h-4 w-4 text-muted-foreground" />}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {isMobile ? (
              <Button variant="outline" className="w-full justify-between">
                {LANGUAGE_CONFIGS.find(c => c.code === language)?.nativeName || language.toUpperCase()}
                <ChevronDownIcon className={"w-4 h-4"} />
              </Button>
            ) : (
              <div className={"flex items-center cursor-pointer"}>
                {currentConfig?.nativeName || language.toUpperCase()}
                <ChevronDownIcon className={"w-4 h-4"} />
              </div>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {LANGUAGE_CONFIGS.map((config) => (
              <DropdownMenuItem
                key={config.code}
                onClick={() => handleLanguageChange(config.code)}
                className="flex items-center gap-2"
              >
                <span>{config.nativeName}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {showIcon && <Globe className="h-4 w-4 text-muted-foreground" />}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {isMobile ? (
            <Button variant="outline" className="w-full justify-between">
              {LANGUAGE_CONFIGS.find(c => c.code === language)?.nativeName || language.toUpperCase()}
              <ChevronDownIcon className={"w-4 h-4"} />
            </Button>
          ) : (
            <div className="flex items-center">
              <div className={"flex items-center cursor-pointer"}>
                {currentConfig?.nativeName || language.toUpperCase()}
                <ChevronDownIcon className={"w-4 h-4"} />
              </div>
            </div>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent className="md:w-[--radix-dropdown-menu-trigger-width]">
          {LANGUAGE_CONFIGS.map((config) => (
            <DropdownMenuItem
              key={config.code}
              onClick={() => handleLanguageChange(config.code)}
              className="flex items-center justify-between w-full"
            >
              <span>{config.nativeName}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// 导出类型
export type { LanguageSwitcherProps };
